import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

import i18n from '@/i18n';
import { recordConsoleError } from '@/lib/diagnostics/diagnosticsBuffer';
import { collectDiagnostics } from '@/lib/diagnostics/collectDiagnostics';
import { formatDiagnosticsText } from '@/lib/diagnostics/diagnosticsReport';

/**
 * AppErrorBoundary — 全站錯誤攔截(R224)
 * =============================================================================
 *
 * 在此之前**全 repo 沒有任何 error boundary**([CheckReportPage](../features/assets/check/CheckReportPage.tsx)
 * 的註解「全站無 ErrorBoundary → 白畫面」是準確的;[providers](./providers.tsx) 把它列在
 * 「未來可擴充」)。後果是 React 的預設行為:**任一元件 render / lifecycle 丟例外 → React
 * 卸載整棵樹 → 使用者看到全白頁面**,沒有訊息、沒有出口、重新整理是唯一解,而且
 * console 以外拿不到任何線索。
 *
 * 這在單頁應用是最貴的一種失效:一個深層元件的 `undefined.map()` 會把整個 app(側欄、
 * 導覽、其他分頁)一起帶走。R199/P-79.2 那類「換 id 不換圖」的資料形狀事故,只要有一次
 * 落在 render path 上就是白畫面。
 *
 * ## 設計
 *
 * - **掛最外層**(`Providers` 的最外圈):連 i18n / Toast provider 自己爆掉也接得住。
 * - **不吃掉診斷**:`componentDidCatch` 一律 `console.error` 原始 error + component stack。
 *   boundary 的價值是「不要白畫面」,不是「假裝沒事」。
 * - **給得出出口**:重新載入按鈕(`location.reload()`)。不做「就地復原」—— 出錯後的
 *   state 已不可信,假裝能繼續用比白畫面更糟。
 * - **文案走 i18n 但不依賴 React context**:用 `i18n.t` 單例(class component 不能用 hook,
 *   而且此時 provider 可能正是壞掉的那個)。i18n 本身也壞掉 → `catch` 退英文字面值,
 *   fallback 永遠 render 得出來。
 *
 * ⚠️ **error boundary 接不到的四類**(React 契約,非本檔缺陷):事件處理器內的例外、
 * 非同步 callback(`setTimeout` / `.then`)、SSR、以及 boundary 自己 render 時丟的例外。
 * 那幾類本來就走各自的 try/catch + toast 路徑。
 */

interface State {
  error: Error | null;
  /** R249 — 診斷碼已複製(短暫回饋;白畫面時沒有 Toast 可用)。 */
  copied: boolean;
}

/**
 * i18n 取字;連 i18n 都壞掉時退英文字面值(fallback 不可以再爆一次)。
 *
 * 取字包成 thunk 而不是傳 key 字串:`i18n.t` 的 key 是 typed union(`resources.d.ts`),
 * 呼叫點寫字面值才吃得到「key 打錯 → tsc 紅」的保護;收成 `string` 參數會把它退化掉。
 */
function tr(read: () => string, fallback: string): string {
  try {
    const s = read();
    return typeof s === 'string' && s.length > 0 ? s : fallback;
  } catch {
    return fallback;
  }
}

export class AppErrorBoundary extends Component<PropsWithChildren, State> {
  override state: State = { error: null, copied: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, copied: false };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 唯一的診斷出口 — 別靜默。component stack 是定位「哪一顆元件」的關鍵。
    console.error('[AppErrorBoundary] uncaught render error', error, info.componentStack);
    // R249 — 同一筆交進診斷緩衝。白畫面是最需要回報的一種故障,而 boundary 不會 reload,
    // buffer 還活著 → 這條路徑交出的診斷含完整 API 足跡 + 這裡的 component stack。
    // 直接呼叫而不是 patch `console.error`,理由見 lib/diagnostics/consoleTrail.ts 檔頭。
    recordConsoleError(
      'error',
      `${error.name}: ${error.message}`,
      [error.stack, info.componentStack].filter(Boolean).join('\n'),
    );
  }

  /**
   * 蒐集 + 複製。所有步驟包在 try 裡 —— 這是**出錯之後**的程式碼路徑,它自己再丟一次
   * 例外就沒有任何東西接得住了(boundary 已經在處理錯誤中)。
   */
  private handleCopyDiagnostics(): void {
    try {
      const report = collectDiagnostics({ category: 'bug', note: '' });
      void navigator.clipboard
        .writeText(formatDiagnosticsText(report))
        .then(() => this.setState({ copied: true }))
        .catch(() => {
          // 剪貼簿被策略拒絕:診斷已在 console(componentDidCatch 印過),不再加干擾。
        });
    } catch {
      // 蒐集失敗(store / config 也壞了)—— 靜默;下面的錯誤細節摺疊區仍可手抄。
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div role="alert" style={wrapStyle} data-testid="app-error-boundary">
        <div style={panelStyle}>
          <div style={titleStyle}>{tr(() => i18n.t('errorBoundary.title'), 'Something went wrong')}</div>
          <p style={bodyStyle}>{tr(() => i18n.t('errorBoundary.body'), 'Please reload the page and try again.')}</p>
          {/* R309(docs/54 §S.5;owner 選項 ①)— **診斷緩衝刻意 in-memory 不持久化**(R249 零 PII 落地),
              而白畫面時使用者的第一個動作就是按「重新載入」→ 那一刻例外與整條 API 足跡一起被清空,
              於是最需要回報的故障剛好交出**空的** trail(beta FB-20260901-0001 的實錘:apiTrail 全 200、
              consoleTrail 空,其實都是 reload 之後才累積的)。修法 = 把順序講出來 + 讓複製成為主動作。 */}
          <p style={copyFirstStyle} data-testid="app-error-copy-first">
            {tr(
              () => i18n.t('errorBoundary.copyFirst'),
              'Copy the diagnostic code first — reloading clears it.',
            )}
          </p>
          {/* R249 — 白畫面是最需要回報的一種故障,而這條路徑的診斷最完整(API 足跡 +
              上面 componentDidCatch 交進去的 component stack)。
              ⚠️ 這裡刻意**不 render 完整的回報對話框**:那支用 `useTranslation` hook,
              而本 boundary 在 `I18nextProvider` 外圈 —— 一旦 i18n 正是壞掉的那個環節,
              dialog 就會在 boundary 自己的 render 裡丟例外,而那是 React 唯一接不住的
              情況(= 又變回白畫面)。一顆「複製診斷碼」不需要 hook、不需要 store,
              也正好對應白畫面時使用者唯一想做的事:把資訊交出去。 */}
          <button
            type="button"
            onClick={() => this.handleCopyDiagnostics()}
            style={btnStyle}
            data-testid="app-error-report"
          >
            {this.state.copied
              ? tr(() => i18n.t('report.copied'), 'Copied')
              : tr(() => i18n.t('report.errorBoundaryAction'), 'Report this error')}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={secondaryBtnStyle}
            data-testid="app-error-reload"
          >
            {tr(() => i18n.t('errorBoundary.reload'), 'Reload')}
          </button>
          {/* 錯誤訊息本身不翻譯(是給回報用的原文);摺疊起來不干擾主要動作。 */}
          <details style={detailsStyle}>
            <summary style={summaryStyle}>{tr(() => i18n.t('errorBoundary.details'), 'Error details')}</summary>
            <pre style={preStyle} data-testid="app-error-message">
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

// ─── styles(theme token;boundary 自己不可依賴任何元件庫)────────────────────────

const wrapStyle = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  background: 'var(--bg-base, #F4F6F9)',
} as const;

const panelStyle = {
  maxWidth: 480,
  width: '100%',
  padding: '28px 24px',
  borderRadius: 'var(--r-lg, 12px)',
  border: '1px solid var(--hairline, #E2E8F0)',
  background: 'var(--bg-card, #FFFFFF)',
  textAlign: 'center',
} as const;

const titleStyle = {
  fontSize: 17,
  fontWeight: 700,
  color: 'var(--text-strong, #0F172A)',
  marginBottom: 8,
} as const;

const bodyStyle = {
  fontSize: 13.5,
  lineHeight: 1.7,
  color: 'var(--ink-500, #5B697F)',
  margin: '0 0 18px',
} as const;

const btnStyle = {
  padding: '9px 20px',
  borderRadius: 'var(--r-sm, 8px)',
  border: 'none',
  background: 'var(--btn-solid, #0F766E)',
  color: 'var(--on-accent, #FFFFFF)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
} as const;

/**
 * **R309 起的次要動作是「重新載入」**(主動作 = 複製診斷碼;順序與視覺層級一起翻,
 * 理由見 render 內的註解)。淡底描邊。
 *
 * token 一律帶 fallback 字面值:與本檔其他樣式同一個理由 —— 這支要在「連
 * theme.css 都可能沒載入」的情況下 render 得出看得見的按鈕。
 */
const secondaryBtnStyle = {
  marginLeft: 10,
  padding: '9px 18px',
  borderRadius: 'var(--r-sm, 8px)',
  border: '1px solid var(--hairline-strong, #CBD5E1)',
  background: 'transparent',
  color: 'var(--text-primary, #1F2937)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
} as const;

/** R309 — 「先複製再重新載入」的引導句(比 body 小一階,緊貼按鈕列)。 */
const copyFirstStyle = {
  fontSize: 12.5,
  lineHeight: 1.7,
  color: 'var(--ink-500, #5B697F)',
  margin: '0 0 14px',
} as const;

const detailsStyle = { marginTop: 18, textAlign: 'left' } as const;

const summaryStyle = {
  fontSize: 12,
  color: 'var(--ink-500, #5B697F)',
  cursor: 'pointer',
} as const;

const preStyle = {
  marginTop: 8,
  padding: 10,
  borderRadius: 'var(--r-sm, 8px)',
  background: 'var(--slate-100, #F1F5F9)',
  color: 'var(--ink-500, #5B697F)',
  fontSize: 11.5,
  fontFamily: 'var(--font-mono, monospace)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 180,
  overflow: 'auto',
} as const;
